// Geometry utilities for computational geometry operations on coordinates

import { destination, bearing, point, distance, round, angle } from '@turf/turf';

/** 
 * These functions deal with lines defined by two points eg
 * [
    { lat: 0, lon: 1 },
    { lat: 1, lon: 1 }
  ]
*/

export function lineMidpoint(line) {
  return ({
    lat: (line[0].lat + line[1].lat) / 2,
    lon: (line[0].lon + line[1].lon) / 2
  });
}

export function lineToPoints(line) {
  return [
    { lat: line[0].lat, lon: line[0].lon },
    { lat: line[1].lat, lon: line[1].lon }
  ];
}

/**  dot product between vector A->B and point P
is =(x−x1)(y2−y1)−(y−y1)(x2−x1)
where A=x1,y1 B=x2,y2 P=x,y
x(lon) y(lat)
*/
export function sideOfLine(frame, A, B) {
  return (frame.lon - A.lon)*(B.lat - A.lat)-(frame.lat - A.lat)*(B.lon - A.lon);
}


export function checkCrossed(p1, p2, line) {
  if (!p1 || !p2) return false;

  if (pointBeyondLineExtent(line, p2)) {
    // console.log("beyond extent");
    return false;
  }

  const prevSide = sideOfLine(p1, line[0], line[1]);
  const currSide = sideOfLine(p2, line[0], line[1]);
  if ((prevSide == 0 || currSide == 0) && prevSide != currSide) return true;
  return Math.sign(prevSide) != Math.sign(currSide);
  // return (prevSide <= 0 && currSide) > 0 || (currSide <= 0 && prevSide > 0);
}

function pointBeyondLineExtent(line, p2) {
  const lineStartDistToP2 = distanceM(line[0], p2);
  const lineStartBearingToP2 = bearingBetweenPoints(line[0], p2);
  const lineBearing = bearingBetweenPoints(line[0], line[1]);
  const lineLength = distanceM(line[0], line[1]);
  const lengthAlongLineP2 = projectVector(lineBearing, lineStartDistToP2, lineStartBearingToP2);
  return lengthAlongLineP2 < 0 || lengthAlongLineP2 > lineLength;
}

export function bearingBetweenPoints(p1, p2) {
  const b = bearing(point([p1.lon, p1.lat]), point([p2.lon, p2.lat]));
  return (b + 360) % 360;
}

export function courseLegBoundary(prevPoint, mark, nextMark, rounding) {
  const arrivalBearing = Math.round(bearingBetweenPoints(prevPoint, mark));
  const departureBearing = Math.round(bearingBetweenPoints(mark, nextMark));
  const angle = sectorAngle(arrivalBearing, departureBearing, rounding);
  const end = destination(point([mark.lon, mark.lat]), 100, angle);
  return [{lat: mark.lat, lon: mark.lon}, { lat: end.geometry.coordinates[1], lon: end.geometry.coordinates[0] }];
}

export function courseLegBoundaryPerp(prevPoint, mark, nextMark, rounding) {
  //use end line 100 km wide at end of leg, perpendicular to bearing from mark to nextMark
  const arrivalBearing = bearingBetweenPoints(prevPoint, mark);
  const departureBearing = bearingBetweenPoints(mark, nextMark);
  
  // Determine if turn is clockwise or anticlockwise
  let bearingDiff = (departureBearing - arrivalBearing + 360) % 360;
  const isClockwise = bearingDiff > 0 && bearingDiff <= 180;
  
  // Perpendicular to arrival bearing: 270° for clockwise, 90° for anticlockwise
  const perpAngle = isClockwise ? 270 : 90;
  const boundaryBearing = arrivalBearing + bearingDelta(0, perpAngle);
  
  const distance = 100000; // 100km
  const boundaryEnd = pointAtBearingDistance(mark, boundaryBearing, distance);
  
  return [mark, boundaryEnd];
}

export function sectorAngle(arrivalBearing, departureBearing, rounding) {
  const clockwise = rounding == 'S';
  const arrivalBearingRev = (arrivalBearing - 180 + 360) % 360;
  const rotation = markRoundingRotationAngle(arrivalBearingRev, departureBearing, clockwise);
  return (arrivalBearingRev + rotation / 2 + 360) % 360;
}

export function markRoundingRotationAngle(fromBearing, toBearing, clockwise) {
  const delta = rotationAngleDir(fromBearing, toBearing, clockwise);
  let rot = delta;
  if (rot > 0 && rot < 180) rot=rot+360;
  if (rot < 0 && rot > -180) rot=rot-360;
  if (rot == 0) rot = clockwise ? 360 : -360;
  return rot;
}

export function rotationAngleDir(from, to, clockwise) {
  if (clockwise) {
    return (from <= to) ? (to - from) : (360 - (from - to));
  } else {
    return (from >= to) ? -(from - to) : -(360 - (to - from));
  }
}

export function projectVector(angleDeg, distance, projectAngleDeg) {
    const angleDiff = ((angleDeg - projectAngleDeg + 540) % 360) - 180; // Range [-180, 180]
    const projected = distance * Math.cos(angleDiff * Math.PI / 180);
    return projected;
}

export function perpendicularDistanceToLine(point, lineStart, lineEnd) {
  // Calculate perpendicular distance from point to line defined by lineStart and lineEnd
  const distanceFromStart = distanceM(lineStart, point);
  const lineBearing = bearingBetweenPoints(lineStart, lineEnd);
  const pointBearing = bearingBetweenPoints(lineStart, point);
  
  // Perpendicular distance is the sine component
  const angleDiff = ((lineBearing - pointBearing + 540) % 360) - 180;
  const perpDistance = Math.abs(distanceFromStart * Math.sin(angleDiff * Math.PI / 180));
  
  return perpDistance;
}

export function distanceM(p1, p2) {
  return distance(point([p1.lon, p1.lat]), point([p2.lon, p2.lat]), { units: 'meters' });
}

/**
 * Calculates dimensions for geometry that have a min and max size but otherwise scale to be a fraction of screen size
 * 
 * @param {*} map - MapLibre map instance
 * @param {*} minSizeMeters 
 * @param {*} maxSizeMeters 
 * @param {*} screenWidthFraction 
 * @param {*} lat 
 * @returns size in meters
 */
export function sizeMetersWithBounds(map, minSizeMeters, maxSizeMeters, screenWidthFraction, lat) {
  return Math.min(sizeMetersWithMin(map, minSizeMeters, screenWidthFraction, lat), maxSizeMeters);
}

export function sizeMetersWithMin(map, minSizeMeters, screenWidthFraction, lat) {
  const zoom = map.getZoom();
  const metersPerPixel = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
  return Math.max(minSizeMeters, getMapPixelWidth(map) * screenWidthFraction * metersPerPixel);
}

export function getMapWidthMeters(map, lat) {
  return getMapPixelWidth(map) * getMetersPerPixel(map, lat);
}

export function getMapPixelWidth(map) {
  return map.getContainer().clientWidth * window.devicePixelRatio;
}

export function getMetersPerPixel(map, lat) {
  const zoom = map.getZoom();
  return 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
}

export function getMaxMinSizeMeters(map, lat, maxPixels = 3, minPixels = 2) {
  const metersPerPixel = getMetersPerPixel(map, lat);
  const sizePixels = getMaxMinSizePixels(map, lat, maxPixels, minPixels);
  return sizePixels * metersPerPixel;
}

export function getMaxMinSizePixels(map, lat, max = 3, min = 2) {
  const zoom = map.getZoom();
  const metersPerPixel = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
  return Math.max(max / metersPerPixel, min);
}

export function getMinSizePixels(map, lat, min = 2) {
  const zoom = map.getZoom();
  const metersPerPixel = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
  return Math.max(metersPerPixel, min);
}


export function pointAtBearingDistance(startPoint, bearingDeg, distanceMeters) {
  const dest = destination(point([startPoint.lon, startPoint.lat]), distanceMeters / 1000, bearingDeg);
  return { lat: dest.geometry.coordinates[1], lon: dest.geometry.coordinates[0] };
}


export function bearingDelta(from, to, accessFn = (e) => e) {
  return (accessFn(to) - accessFn(from) + 540) % 360 - 180;
}


export function checkExitedCircle(prevFrame, frame, circleCenter, radiusM) {
  const prevDist = distanceM(prevFrame, circleCenter);
  const currDist = distanceM(frame, circleCenter);
  return prevDist <= radiusM && currDist > radiusM;
}


export function normaliseTo0To360(angle) {
  const result = (angle + 10 * 360) % 360;
  return result != 360 ? result : 0;
}

export function normaliseTo180To180(angle) {
  return (angle + 540) % 360 - 180;
}

export function bearingsEqual(a1,a2) {
  return normaliseTo0To360(a1) === normaliseTo0To360(a2);
}

// unwraps target (any degrees) relative to lastAngle so consecutive values never
// jump by more than 180deg — keeps linear angle transitions (eg deck.gl getAngle)
// rotating the short way instead of spinning through the 0/360 wraparound.
export function unwrapAngle(lastAngle, target) {
  if (lastAngle == null) return target;
  return lastAngle + bearingDelta(lastAngle, target);
}